import { Component, OnInit, AfterViewInit, OnDestroy, Input, Output, EventEmitter, ChangeDetectorRef, ViewChild, ElementRef, TemplateRef, EmbeddedViewRef } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Overlay, OverlayRef, OverlayConfig } from '@angular/cdk/overlay';
import { TemplatePortalDirective } from '@angular/cdk/portal';

import { FieldComponent, UndoAction } from './field.component';
import { FormField, Link, Disclaimer } from './form-field.model';
import { DynamicOverlaysDirective } from './dynamic-overlays.directive';

@Component({
  selector: 'app-checkbox-field',
  templateUrl: './checkbox-field.component.html',
  styleUrls: ['./checkbox-field.component.scss']
})
export class CheckboxFieldComponent extends FieldComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() draftData: any;
  @Input() dynamicOverlays: DynamicOverlaysDirective;
  @Input() field: FormField;
  @Input() locked: boolean = false;
  @Input() lockValues: boolean = false;
  @Input() templateData: any;
  @Input() workflowId: number;
  @Output() focusChanged = new EventEmitter<boolean>();
  @Output() highlightChanged = new EventEmitter<{highlighted: boolean, color?: string}>();
  @Output() resized = new EventEmitter<FieldComponent>();
  @Output() returnKey = new EventEmitter<FieldComponent>();
  @Output() undoableAction = new EventEmitter<UndoAction>();
  @Output() updateLinked = new EventEmitter<{link: Link, value?: any, categories?: any[], enabled?: boolean}>();
  @Output() viewLoaded = new EventEmitter<FieldComponent>();
  @ViewChild("versionTemplate") versionTemplate: TemplateRef<null>;
  @ViewChild("versionElement") versionElement: ElementRef;

  defaultValue: boolean;
  disclaimer: Disclaimer;
  focused: boolean = false;
  id: string;
  initialized: boolean = false;
  highlightColor: string;
  highlightValues: boolean[];
  highlighted: boolean = false;
  label: string;
  previousValue: boolean;
  required: boolean = false;
  showingPreviousValue: boolean = false;
  value: boolean;
  versionChanged: boolean = false;
  versionView: EmbeddedViewRef<null>;
  get blank(): boolean { return this.value !== true }
  get touched(): boolean {return this.control != null && this.control.touched}
  get valid(): boolean {return this.locked == true || (this.control != null && this.control.valid)}

  constructor(
    cd: ChangeDetectorRef,
    private overlay: Overlay) { super(cd) }

  ngOnInit() {
    if (this.id == null) {this.id = this.field.id}
    this.label = this.field.label;
    this.required = this.field.required;
    this.highlightColor = this.field.highlightColor;
    this.highlightValues = this.field.highlightValues;
    if (this.field.locked) { this.locked = true }

    this.loadDefaultValue();
    this.loadInitialState(this.draftData);
  }

  ngAfterViewInit() {
    this.viewLoaded.emit(this);
  }

  ngOnDestroy() {
    this.hidePreviousValue();
  }

  loadDefaultValue() {
    let templateValue: boolean;
    if (this.templateData != null) {
      templateValue = this.templateData instanceof Array ? this.templateData[0].stored : this.templateData
    }

    if (templateValue != null) {
      this.defaultValue = ''+templateValue === "true"
      if (this.lockValues) { this.locked = true };
    } else if (this.field.defaultValue != null) {
      this.defaultValue = ''+this.field.defaultValue.stored === "true";
    }
  }

  loadInitialState(draftValue?: boolean) {
    let validators = [];
    if (this.required === true) {validators.push(Validators.required)}

    this.value = draftValue != null ? draftValue : this.defaultValue;
    this.control = new FormControl({value: this.value, disabled: this.locked}, validators);
    this.control.valueChanges.subscribe((value) => {
      this._dirty = this.initialized && !this.valuesEqual(value, this.value);
      this.value = value;
      this.initialized = true;
      this.emitValue();
    })

    // Emit initial validity and value
    this.control.updateValueAndValidity();
  }

  emitValue() {
    this.valueChanged.emit({field: this.id, value: this.value});
    this.checkHighlight();
  }

  setValue(value: any, emit: boolean = true) {
    this.value = value;
    this.control.setValue(value, {emitEvent: false});
    if (emit) { this.emitValue(); }
  }

  setPreviousValue(value) {
    this.previousValue = value;
    this.versionChanged = this.value !== this.previousValue;
    if (this.versionChanged) {
      this.showPreviousValue();
    }
    this.cd.markForCheck();
  }

  resetPreviousValue() {
    this.previousValue = null;
    this.versionChanged = false;
    this.hidePreviousValue();
    this.cd.markForCheck();
  }

  onFocus(event) {
    this.focused = true;
    this.focusChanged.emit(true);
    return true;
  }

  onBlur(event) {
    this.focused = false;
    this.focusChanged.emit(false);
    return true;
  }

  reset() {
    this.loadInitialState();
    this.cd.markForCheck();
  }

  compare(other): number {
    let a = this.value;
    let b = other.value;
    return a === b ? 0 : (a ? -1 : 1);
  }

  updateLinkedFields() { }
  
  setCategories(category) { }

  checkHighlight() {
    let value = this.value != null ? this.value : false;
    if (this.highlightValues != null) {
      let match = false;
      for (let v of this.highlightValues) {
        if (v === this.value) {
          match = true;
          break;
        }
      }
      this.highlighted = match;
      this.highlightChanged.emit({highlighted: this.highlighted});
    }
  }

  showPreviousValue() {
    this.showingPreviousValue = true;
    let container = this.dynamicOverlays.viewContainerRef;
    this.versionView = container.createEmbeddedView(this.versionTemplate);
    this.positionOverlays();
  }

  hidePreviousValue() {
    if (this.versionView != null) {
      this.versionView.destroy();
    }
    this.showingPreviousValue = false;
  }

  togglePreviousValue() {
    if (this.showingPreviousValue) {
      this.hidePreviousValue();
    } else {
      this.showPreviousValue();
    }
  }

  positionOverlays() {
    if (this.versionView != null) {
      let rect = this.versionElement.nativeElement.getBoundingClientRect();
      let rootNode = this.versionView.rootNodes.find((n) => n.tagName == 'DIV');
      rootNode.style.left = '' + (rect.left + window.pageXOffset) + 'px';
      rootNode.style.top = '' + (rect.top + window.pageYOffset) + 'px';
      rootNode.style.maxWidth = '' + rect.width + 'px';
    }
  }

  setPatternValue(field: string, value: any) { }

  undo(action: UndoAction) { }
}


// WEBPACK FOOTER //
// ./src/app/forms/checkbox-field.component.ts